import { API_ENDPOINTS } from '../config/api.config';
import { apiGet, apiPost, apiPut } from '../utils/apiClient';
import type {
  CriticalityBand,
  EscalationMatrix,
  EscalationMatrixPayload,
  EscalationMatrixStatus,
} from '../types/escalationMatrix.types';
import type { EscalationContactRole } from '../types/escalationContact.types';

function firstNumber(...values: unknown[]): number | null {
  for (const value of values) {
    if (value === undefined || value === null) {
      continue;
    }
    return Number(value);
  }
  return null;
}

interface ApiMatrixResponse {
  success?: boolean;
  message?: string;
  count?: number;
  validationErrors?: Array<{
    field?: string;
    levelNumber?: number;
    criticalityBand?: CriticalityBand;
    message: string;
  }>;
  data?: Record<string, unknown> | Record<string, unknown>[];
}

function mapLevel(raw: Record<string, unknown>) {
  const contact = raw.contact as Record<string, unknown> | null | undefined;
  return {
    id: raw.id != null ? Number(raw.id) : undefined,
    matrixId: raw.matrixId != null ? Number(raw.matrixId ?? raw.matrix_id) : undefined,
    levelNumber: Number(raw.levelNumber ?? raw.level_number),
    orderIndex: Number(raw.orderIndex ?? raw.order_index),
    role: raw.role as EscalationContactRole,
    contactId: firstNumber(raw.contactId, raw.contact_id),
    timerMinutes: Number(raw.timerMinutes ?? raw.timer_minutes ?? 0),
    contact: contact
      ? {
          id: Number(contact.id),
          facilityId: Number(contact.facilityId ?? contact.facility_id),
          name: String(contact.name ?? ''),
          role: contact.role as EscalationContactRole,
          department: (contact.department as string) ?? null,
          mobileNumber: String(contact.mobileNumber ?? contact.mobile_number ?? ''),
          alternateMobileNumber: (contact.alternateMobileNumber ??
            contact.alternate_mobile_number) as string | null,
          status: (contact.status as 'Active' | 'Inactive') ?? 'Active',
        }
      : null,
  };
}

function mapMatrix(raw: Record<string, unknown>): EscalationMatrix {
  const levels = Array.isArray(raw.levels)
    ? raw.levels.map(level => mapLevel(level as Record<string, unknown>))
    : [];

  return {
    id: Number(raw.id),
    facilityId: Number(raw.facilityId ?? raw.facility_id),
    criticalityBand: (raw.criticalityBand ?? raw.criticality_band) as CriticalityBand,
    reminderIntervalMinutes: Number(
      raw.reminderIntervalMinutes ?? raw.reminder_interval_minutes ?? 5
    ),
    maxReminderAttempts: Number(raw.maxReminderAttempts ?? raw.max_reminder_attempts ?? 3),
    status: (raw.status as EscalationMatrixStatus) ?? 'Draft',
    activatedAt: (raw.activatedAt ?? raw.activated_at) as string | null | undefined,
    activatedBy: firstNumber(raw.activatedBy, raw.activated_by),
    levels,
    createdAt: raw.createdAt as string | undefined,
    updatedAt: raw.updatedAt as string | undefined,
  };
}

export async function listEscalationMatrices(
  facilityId: number
): Promise<{ success: boolean; data: EscalationMatrix[]; error?: string }> {
  try {
    const url = `${API_ENDPOINTS.ESCALATION_MATRIX.MATRIX}?facilityId=${facilityId}`;
    const response = await apiGet<ApiMatrixResponse>(url, { requireAuth: true });
    const body = response.data;
    if (body?.success === false) {
      return { success: false, data: [], error: body.message || 'Failed to load matrices' };
    }
    const rows = (Array.isArray(body?.data) ? body.data : []).map(item =>
      mapMatrix(item as Record<string, unknown>)
    );
    return { success: true, data: rows };
  } catch (err) {
    return {
      success: false,
      data: [],
      error: err instanceof Error ? err.message : 'Failed to load matrices',
    };
  }
}

export async function getEscalationMatrixByBand(
  facilityId: number,
  criticalityBand: CriticalityBand
): Promise<{ success: boolean; data?: EscalationMatrix; error?: string; notFound?: boolean }> {
  try {
    const url = API_ENDPOINTS.ESCALATION_MATRIX.GET_BY_BAND(facilityId, criticalityBand);
    const response = await apiGet<ApiMatrixResponse>(url, { requireAuth: true });
    const body = response.data;
    if (body?.success === false || !body?.data) {
      return { success: false, error: body?.message || 'Matrix not found', notFound: true };
    }
    return { success: true, data: mapMatrix(body.data as Record<string, unknown>) };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to load matrix';
    return { success: false, error: message, notFound: /not found|404/i.test(message) };
  }
}

export async function createEscalationMatrix(payload: EscalationMatrixPayload) {
  try {
    const response = await apiPost<ApiMatrixResponse>(
      API_ENDPOINTS.ESCALATION_MATRIX.MATRIX,
      payload,
      { requireAuth: true }
    );
    const body = response.data;
    if (body?.success === false || !body?.data) {
      return {
        success: false as const,
        error: body?.message || 'Failed to create matrix',
        validationErrors: body?.validationErrors,
      };
    }
    return { success: true as const, data: mapMatrix(body.data as Record<string, unknown>) };
  } catch (err) {
    return {
      success: false as const,
      error: err instanceof Error ? err.message : 'Failed to create matrix',
    };
  }
}

export async function updateEscalationMatrix(matrixId: number, payload: EscalationMatrixPayload) {
  try {
    const response = await apiPut<ApiMatrixResponse>(
      API_ENDPOINTS.ESCALATION_MATRIX.UPDATE_MATRIX(matrixId),
      payload,
      { requireAuth: true }
    );
    const body = response.data;
    if (body?.success === false || !body?.data) {
      return {
        success: false as const,
        error: body?.message || 'Failed to update matrix',
        validationErrors: body?.validationErrors,
      };
    }
    return { success: true as const, data: mapMatrix(body.data as Record<string, unknown>) };
  } catch (err) {
    return {
      success: false as const,
      error: err instanceof Error ? err.message : 'Failed to update matrix',
    };
  }
}

export async function activateEscalationMatrix(matrixId: number, facilityId: number) {
  try {
    const response = await apiPost<ApiMatrixResponse>(
      API_ENDPOINTS.ESCALATION_MATRIX.ACTIVATE(matrixId),
      { facilityId },
      { requireAuth: true }
    );
    const body = response.data;
    if (body?.success === false || !body?.data) {
      return {
        success: false as const,
        error: body?.message || 'Failed to activate matrix',
        validationErrors: body?.validationErrors,
      };
    }
    return { success: true as const, data: mapMatrix(body.data as Record<string, unknown>) };
  } catch (err) {
    return {
      success: false as const,
      error: err instanceof Error ? err.message : 'Failed to activate matrix',
    };
  }
}
